/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   metadataService.ts
 *     Feature         :   183-external-metadata-enrichment
 *     Description     :   Frontend API service for the Metadata section —
 *                         semantic model definitions, item lineage, refresh
 *                         history/schedules, and item schedules.
 *     Constitution    :   v1.18.0
 *     Author          :   Peter Dobler
 *     Created         :   2026-06-07
 * =========================================================================================
 */

import { apiClient } from '../apiClient';
import { useQuery } from '@tanstack/react-query';
import type {
  MetadataOverview,
  SemanticModelDefinition,
  ItemLineage,
  DatasetRefreshHistory,
  DatasetRefreshSchedule,
  ItemSchedule,
} from '@shared/types/metadata';

// ── Query key factory ─────────────────────────────────────────────────────────

export const METADATA_KEYS = {
  all: ['metadata'] as const,
  overview: () => [...METADATA_KEYS.all, 'overview'] as const,
  semanticModels: () => [...METADATA_KEYS.all, 'semantic-models'] as const,
  semanticModelsList: () => [...METADATA_KEYS.semanticModels(), 'list'] as const,
  definition: (id: string) => [...METADATA_KEYS.semanticModels(), id, 'definition'] as const,
  lineage: (id: string) => [...METADATA_KEYS.all, 'lineage', id] as const,
  lineageList: () => [...METADATA_KEYS.all, 'lineage', 'list'] as const,
  refreshHistory: (id: string, limit: number) => [...METADATA_KEYS.all, 'refresh-history', id, limit] as const,
  refreshHistoryList: () => [...METADATA_KEYS.all, 'refresh-history', 'list'] as const,
  refreshSchedule: (id: string) => [...METADATA_KEYS.all, 'refresh-schedule', id] as const,
  itemSchedules: (id: string) => [...METADATA_KEYS.all, 'item-schedules', id] as const,
  schedulesList: () => [...METADATA_KEYS.all, 'schedules', 'list'] as const,
};

// ── List item types (returned by new list endpoints) ─────────────────────────

export interface SmListItem {
  id: string;
  displayName: string;
  workspaceName: string;
  tableCount: number;
  measureCount: number;
}

export interface LineageListItem {
  id: string;
  itemName: string;
  itemType: string;
  workspaceName: string;
  sourceCount: number;
}

export interface RefreshHistoryListItem {
  id: string;
  displayName: string;
  workspaceName: string;
  refreshCount: number;
  lastRefresh: string | null;
}

export interface ScheduleListItem {
  id: string;
  itemName: string;
  itemType: string;
  workspaceName: string;
  scheduleCount: number;
}

// ── API functions ─────────────────────────────────────────────────────────────

export async function getMetadataOverview(): Promise<MetadataOverview> {
  const { data } = await apiClient.get<MetadataOverview>('/api/metadata/overview');
  return data;
}

export async function getSemanticModelDefinition(id: string): Promise<SemanticModelDefinition | null> {
  const { data } = await apiClient.get<SemanticModelDefinition | null>(`/api/metadata/semantic-models/${id}/definition`);
  return data;
}

export async function getItemLineage(id: string): Promise<ItemLineage | null> {
  const { data } = await apiClient.get<ItemLineage | null>(`/api/metadata/items/${id}/lineage`);
  return data;
}

export async function getRefreshHistory(id: string, limit = 50): Promise<DatasetRefreshHistory | null> {
  const { data } = await apiClient.get<DatasetRefreshHistory | null>(`/api/metadata/datasets/${id}/refresh-history`, {
    params: { limit },
  });
  return data;
}

export async function getRefreshSchedule(id: string): Promise<{ schedule: DatasetRefreshSchedule | null; modelName: string | null } | null> {
  const { data } = await apiClient.get<{ schedule: DatasetRefreshSchedule | null; modelName: string | null } | null>(`/api/metadata/datasets/${id}/schedule`);
  return data;
}

export async function getItemSchedules(id: string): Promise<{ itemName: string; itemType: string; schedules: ItemSchedule[] } | null> {
  const { data } = await apiClient.get<{ itemName: string; itemType: string; schedules: ItemSchedule[] } | null>(`/api/metadata/items/${id}/schedules`);
  return data;
}

// ── React Query hooks ─────────────────────────────────────────────────────────

export function useMetadataOverview() {
  return useQuery({
    queryKey: METADATA_KEYS.overview(),
    queryFn: getMetadataOverview,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSemanticModelDefinition(id: string | null) {
  return useQuery({
    queryKey: METADATA_KEYS.definition(id ?? ''),
    queryFn: () => getSemanticModelDefinition(id!),
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  });
}

export function useItemLineage(id: string | null) {
  return useQuery({
    queryKey: METADATA_KEYS.lineage(id ?? ''),
    queryFn: () => getItemLineage(id!),
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  });
}

export function useRefreshHistory(id: string | null, limit = 50) {
  return useQuery({
    queryKey: METADATA_KEYS.refreshHistory(id ?? '', limit),
    queryFn: () => getRefreshHistory(id!, limit),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRefreshSchedule(id: string | null) {
  return useQuery({
    queryKey: METADATA_KEYS.refreshSchedule(id ?? ''),
    queryFn: () => getRefreshSchedule(id!),
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  });
}

export function useItemSchedules(id: string | null) {
  return useQuery({
    queryKey: METADATA_KEYS.itemSchedules(id ?? ''),
    queryFn: () => getItemSchedules(id!),
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  });
}

// ── List API functions ────────────────────────────────────────────────────────

export async function listSemanticModelsWithDefinitions(): Promise<SmListItem[]> {
  const { data } = await apiClient.get<SmListItem[]>('/api/metadata/semantic-models');
  return data;
}

export async function listLineageItems(): Promise<LineageListItem[]> {
  const { data } = await apiClient.get<LineageListItem[]>('/api/metadata/lineage');
  return data;
}

export async function listRefreshHistoryModels(): Promise<RefreshHistoryListItem[]> {
  const { data } = await apiClient.get<RefreshHistoryListItem[]>('/api/metadata/refresh-history');
  return data;
}

export async function listScheduledItems(): Promise<ScheduleListItem[]> {
  const { data } = await apiClient.get<ScheduleListItem[]>('/api/metadata/schedules');
  return data;
}

// ── List hooks ────────────────────────────────────────────────────────────────

export function useSemanticModelsList() {
  return useQuery({
    queryKey: METADATA_KEYS.semanticModelsList(),
    queryFn: listSemanticModelsWithDefinitions,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLineageList() {
  return useQuery({
    queryKey: METADATA_KEYS.lineageList(),
    queryFn: listLineageItems,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRefreshHistoryList() {
  return useQuery({
    queryKey: METADATA_KEYS.refreshHistoryList(),
    queryFn: listRefreshHistoryModels,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSchedulesList() {
  return useQuery({
    queryKey: METADATA_KEYS.schedulesList(),
    queryFn: listScheduledItems,
    staleTime: 5 * 60 * 1000,
  });
}
